import React, { useState, useEffect, useRef } from 'react'
import { X, ChevronRight, ChevronLeft } from 'lucide-react'

export interface TutorialStep {
  title: string
  description: string
  targetSelector?: string // CSS selector for the element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  highlightPadding?: number
}

interface AppTutorialProps {
  isOpen: boolean
  onClose: () => void
  steps: TutorialStep[]
}

export const AppTutorial: React.FC<AppTutorialProps> = ({ isOpen, onClose, steps }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const step = steps[currentStep]
  const isLastStep = currentStep === steps.length - 1
  const isFirstStep = currentStep === 0

  // Reset to first step when tutorial opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0)
    }
  }, [isOpen])

  // Update highlight position when step changes
  useEffect(() => {
    if (!isOpen || !step.targetSelector) {
      setHighlightRect(null)
      return
    }

    const updateHighlight = () => {
      const targetElement = document.querySelector(step.targetSelector!)
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect()
        setHighlightRect(rect)
        
        // Scroll into view if needed
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        })
      } else {
        setHighlightRect(null)
      }
    }

    // Update immediately and after a short delay to ensure elements are rendered
    updateHighlight()
    const timer = setTimeout(updateHighlight, 100)

    // Update on window resize
    window.addEventListener('resize', updateHighlight)
    window.addEventListener('scroll', updateHighlight, true)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', updateHighlight)
      window.removeEventListener('scroll', updateHighlight, true)
    }
  }, [isOpen, step.targetSelector, currentStep])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      onClose()
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSkip = () => {
    onClose()
    setCurrentStep(0)
  }

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    if (!highlightRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }
    }

    const padding = step.highlightPadding || 12
    const position = step.position || 'right'
    
    const styles: React.CSSProperties = {
      position: 'fixed',
      maxWidth: '400px',
      zIndex: 10002
    }

    switch (position) {
      case 'top':
        styles.left = `${highlightRect.left + highlightRect.width / 2}px`
        styles.bottom = `${window.innerHeight - highlightRect.top + padding}px`
        styles.transform = 'translateX(-50%)'
        break
      case 'bottom':
        styles.left = `${highlightRect.left + highlightRect.width / 2}px`
        styles.top = `${highlightRect.bottom + padding}px`
        styles.transform = 'translateX(-50%)'
        break
      case 'left':
        styles.right = `${window.innerWidth - highlightRect.left + padding}px`
        styles.top = `${highlightRect.top + highlightRect.height / 2}px`
        styles.transform = 'translateY(-50%)'
        break
      case 'right':
        styles.left = `${highlightRect.right + padding}px`
        styles.top = `${highlightRect.top + highlightRect.height / 2}px`
        styles.transform = 'translateY(-50%)'
        break
      case 'center':
        styles.top = '50%'
        styles.left = '50%'
        styles.transform = 'translate(-50%, -50%)'
        break
    }

    return styles
  }

  if (!isOpen) return null

  return (
    <>
      {/* Overlay with cutout for highlighted element */}
      <div className="fixed inset-0 z-[10000]" style={{ pointerEvents: 'none' }}>
        <svg width="100%" height="100%" style={{ pointerEvents: 'auto' }}>
          <defs>
            <mask id="tutorial-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {highlightRect && (
                <rect
                  x={highlightRect.left - (step.highlightPadding || 8)}
                  y={highlightRect.top - (step.highlightPadding || 8)}
                  width={highlightRect.width + (step.highlightPadding || 8) * 2}
                  height={highlightRect.height + (step.highlightPadding || 8) * 2}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.7)"
            mask="url(#tutorial-mask)"
            onClick={handleSkip}
            style={{ cursor: 'pointer' }}
          />
        </svg>

        {/* Highlight border */}
        {highlightRect && (
          <div
            className="absolute border-4 border-blue-500 rounded-lg shadow-lg animate-pulse"
            style={{
              left: `${highlightRect.left - (step.highlightPadding || 8)}px`,
              top: `${highlightRect.top - (step.highlightPadding || 8)}px`,
              width: `${highlightRect.width + (step.highlightPadding || 8) * 2}px`,
              height: `${highlightRect.height + (step.highlightPadding || 8) * 2}px`,
              pointerEvents: 'none'
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border-2 border-blue-500 dark:border-blue-400 p-6 z-[10001]"
        style={{ ...getTooltipStyle(), pointerEvents: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {step.title}
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>步骤 {currentStep + 1} / {steps.length}</span>
              <div className="flex gap-1">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentStep
                        ? 'bg-blue-500'
                        : index < currentStep
                        ? 'bg-blue-300'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors ml-4"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Description */}
        <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
          {step.description}
        </p>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={isFirstStep}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              isFirstStep
                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <ChevronLeft className="h-4 w-4" />
            上一步
          </button>

          <div className="flex gap-2">
            <button
              onClick={handleSkip}
              className="px-4 py-2 rounded-lg font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              跳过
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 rounded-lg font-medium bg-blue-500 hover:bg-blue-600 text-white transition-colors shadow-lg shadow-blue-500/30"
            >
              {isLastStep ? '完成' : '下一步'}
              {!isLastStep && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

